import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Save, Loader, FileText, Trash2, Copy, Upload, X, CheckCircle, AlertCircle, ArrowLeft, Plus, Database, Check, Search, Zap, Building2, User, Info, Lock, Unlock, Eye, EyeOff, KeyRound } from 'lucide-react';
import { settingsService } from '../../../services/SettingsService';
import { organizationService } from '../../../services/OrganizationService';
import AppLayout from '../../../layout/AppLayout';
import MobileHeader from '../../../layout/MobileHeader';
import NotificationModal from '../../../components/common/modals/NotificationModal';

// Brand logos are hotlinked from carlogos.org, which serves them at a slug
// derived from the brand name. Brands it does not carry (DS Automobiles,
// Mercedes-Maybach, Omoda, Maruti Suzuki, Volvo Trucks, Tracks-Excelerator)
// 404 and land on the initials placeholder via handleBrandLogoError.
const BRAND_LOGO_OVERRIDES: Record<string, string> = {
  Multibrand: 'data:image/svg+xml;utf8,' + encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 128 128"><g fill="none" stroke="#374151" stroke-width="7">'
    + '<circle cx="44" cy="52" r="26"/><circle cx="84" cy="52" r="26"/><circle cx="64" cy="84" r="26"/></g></svg>'),
  'Thomas Built Buses': 'https://cdn.freebiesupply.com/logos/large/2x/thomas-built-buses-logo-png-transparent.png',
};

const brandLogoSlug = (brand: string): string =>
  brand
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const getBrandLogo = (brand: string): string =>
  BRAND_LOGO_OVERRIDES[brand] || `https://www.carlogos.org/car-logos/${brandLogoSlug(brand)}-logo.png`;

const getBrandLogoFallback = (brand: string): string =>
  `https://ui-avatars.com/api/?name=${encodeURIComponent(brand)}&background=f3f4f6&color=374151&size=128&bold=true`;

// Degrade to an initials tile rather than a blank box, so a future upstream
// URL change is visible as a placeholder instead of silently empty.
const handleBrandLogoError = (brand: string) => (e: React.SyntheticEvent<HTMLImageElement>) => {
  const img = e.currentTarget;
  if (img.dataset.fallbackApplied === 'true') {
    img.style.display = 'none';
    return;
  }
  img.dataset.fallbackApplied = 'true';
  img.src = getBrandLogoFallback(brand);
};

// OE Brands list. "Multibrand" is a catalog that serves many brands at once
// (partslink24): it is pinned first in the grid and its entry carries the
// brands it answers for (see the brands field in the form).
const MULTIBRAND = 'Multibrand';
// Per-implementation form features. Add an implementation here when its
// vendor needs the field; nothing else in the form knows vendor names.
const MULTIBRAND_IMPLEMENTATIONS = ['partslink24'];          // entry carries a brands list
const COMPANY_ID_IMPLEMENTATIONS = ['partslink24', 'asaweb'];          // login is (company, user, password)
const needsBrands = (impl?: string) => MULTIBRAND_IMPLEMENTATIONS.includes(impl || '');
// Every brand an entry answers for: its own name plus its bound brands.
const catalogBrands = (c: { catalog_name: string; brands?: string[] }) =>
  Array.from(new Set([c.catalog_name, ...(c.brands || [])].filter(Boolean).map(b => b.trim())));
// A brand may belong to ONE entry — routing would otherwise pick silently.
const brandConflicts = (target: UIConfig, all: UIConfig[]) => {
  const mine = catalogBrands(target).map(b => b.toUpperCase());
  const out: { brand: string; other: string }[] = [];
  for (const other of all) {
    if (other._id === target._id || other.catalog_name.toUpperCase() === target.catalog_name.toUpperCase()) continue;
    for (const b of catalogBrands(other)) {
      if (mine.includes(b.toUpperCase())) out.push({ brand: b, other: other.catalog_name });
    }
  }
  return out;
};
const needsCompanyId = (impl?: string) => COMPANY_ID_IMPLEMENTATIONS.includes(impl || '');
const OE_BRANDS = [
  MULTIBRAND,
  'Volkswagen', 'Audi', 'Porsche', 'Škoda', 'SEAT', 'CUPRA', 'Bentley', 'Lamborghini', 'MAN', 'Scania',
  'BMW', 'MINI', 'Rolls-Royce',
  'Mercedes-Benz', 'Mercedes-AMG', 'Mercedes-Maybach',
  'Toyota', 'Lexus', 'Daihatsu', 'Hino',
  'Honda', 'Acura',
  'Nissan', 'Infiniti',
  'Mazda', 'Subaru', 'Mitsubishi', 'Suzuki', 'Isuzu',
  'Chevrolet', 'GMC', 'Cadillac',
  'Ford', 'Lincoln',
  'Tesla', 'Rivian', 'Lucid',
  'Renault', 'Dacia', 'Alpine',
  'Peugeot', 'Citroën', 'DS Automobiles',
  'Fiat', 'Alfa Romeo', 'Maserati', 'Lancia', 'Iveco',
  'Lotus', 'McLaren', 'LEVC',
  'Hyundai', 'Kia', 'Genesis', 'SsangYong',
  'BYD', 'Denza', 'Yangwang',
  'Geely', 'Volvo', 'Polestar', 'Zeekr',
  'NIO', 'XPeng',
  'MG', 'Maxus',
  'Chery', 'Omoda',
  'Tata', 'Jaguar', 'Land Rover', 'Mahindra', 'Maruti Suzuki',
  'Volvo Trucks',
  'Koenigsegg',
  "Freightliner",
  "Western Star",
  "Thomas Built Buses",
  "Tracks-Excelerator"
];

interface EmailOtpConfig {
  enabled: boolean;
  from?: string;
  subject?: string;
  // Read compatibility for configurations saved by the earlier advanced UI.
  match?: {
    sender?: { operator: 'exact_address' | 'contains'; value: string };
    subject?: { operator: 'contains' | 'equals'; value: string };
  };
}

// Backend API types
interface BackendCatalogSettings {
  catalog_name: string;
  catalog_user: string;
  catalog_password: string;
  catalog_pricing_file?: string;
  catalog_pricing_file_name?: string;
  catalog_implementation?: string;
  brands?: string[]; // multi-brand catalogs (partslink24): brands routed to this entry
  catalog_company?: string; // company / client id (partslink24 login has three parts)
  has_credentials?: boolean; // server-side flag; credentials are never sent back to client
  source?: string;      // "shared" = expanded from a platform-managed shared credential
  shared_slug?: string; // slug of the SharedCatalogConfig behind a shared entry
  email_otp?: EmailOtpConfig | null;
}

interface BackendUserSettings {
  language: string;
  catalogs_config: BackendCatalogSettings[];
  available_catalogs?: string[];
  oe_brands?: string[];
  use_organization_settings?: boolean;
  settings_source?: 'organization' | 'personal' | 'default';
}

interface BackendUserSettingsUpdate {
  language: string;
  catalogs_config: BackendCatalogSettings[];
  oe_brands?: string[];
}

// UI-specific config interface
interface UIConfig extends BackendCatalogSettings {
  _id: string;
  catalog_type: string;
  _credentialsEdited?: boolean; // true only if user actually typed new credentials
}

interface CatalogSettingsPanelProps {
  onBack: () => void;
}

const CatalogSettingsPanel: React.FC<CatalogSettingsPanelProps> = ({ onBack }) => {
  const { t } = useTranslation();

  // State
  const [configs, setConfigs] = useState<UIConfig[]>([]);
  // Brands the VIN decoder can produce — options for a multi-brand catalog.
  const [wmiBrands, setWmiBrands] = useState<string[]>([]);
  const [brandFilter, setBrandFilter] = useState('');
  const [isSuggestingBrands, setIsSuggestingBrands] = useState(false);
  const [activeConfigId, setActiveConfigId] = useState<string | null>(null);
  const [tempConfig, setTempConfig] = useState<UIConfig | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [availableCatalogs, setAvailableCatalogs] = useState<string[]>([]);
  const [saveStatus, setSaveStatus] = useState<'none' | 'success' | 'error'>('none');
  const [testStatus, setTestStatus] = useState<'none' | 'success' | 'error'>('none');
  const [selectedBrands, setSelectedBrands] = useState<string[]>([]);
  const [brandSearchQuery, setBrandSearchQuery] = useState('');
  const [credentialsUnlocked, setCredentialsUnlocked] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Organization settings state. Personal catalog settings were removed from
  // this panel: org members always edit org settings; 'personal' only applies
  // to solo users with no organization.
  const [settingsSource, setSettingsSource] = useState<'organization' | 'personal' | 'default'>('default');
  // Shared-credential assignments for this org (from the backend, independent
  // of the merged catalog rows so overridden assignments still show up)
  const [sharedAssignments, setSharedAssignments] = useState<{ catalog_name: string; slug: string; overridden: boolean }[]>([]);
  const [organizationName, setOrganizationName] = useState<string | null>(null);

  // Notification state
  const [notification, setNotification] = useState<{
    show: boolean;
    title: string;
    message: string;
    type: 'success' | 'error' | 'warning' | 'info';
  }>({
    show: false,
    title: '',
    message: '',
    type: 'info'
  });

  // Delete confirmation state
  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    show: boolean;
    configId: string | null;
    configName: string;
  }>({
    show: false,
    configId: null,
    configName: ''
  });

  // Load settings on mount
  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setIsLoading(true);
    try {
      const response = await settingsService.getUserSettings();
      const backendSettings: BackendUserSettings = response.data.settings;
      const organization = response.data.organization;

      setAvailableCatalogs(backendSettings.available_catalogs || []);

      // Set organization info
      if (organization) {
        setOrganizationName(organization.name);
      }

      // If user is part of an organization, load organization settings by default
      if (organization) {
        try {
          const orgResponse = await organizationService.getOrganizationSettings();
          const orgSettings = orgResponse.data;

          // Org members always edit organization settings
          setSettingsSource('organization');
          setSharedAssignments(orgSettings.shared_catalogs || []);

          // Load organization catalogs
          if (orgSettings.catalogs_config && orgSettings.catalogs_config.length > 0) {
            const loadedConfigs = orgSettings.catalogs_config.map((conf: any, index: number) => ({
              ...conf,
              _id: `conf-${Date.now()}-${index}`,
              catalog_type: 'parts_catalog'
            }));
            setConfigs(loadedConfigs);

            const brandsFromConfigs = loadedConfigs
              .map((c: UIConfig) => c.catalog_name)
              .filter((name: string) => OE_BRANDS.some(brand => brand.toUpperCase() === name.toUpperCase()));
            const savedBrands = orgSettings.oe_brands || [];
            const allBrands = Array.from(new Set([...brandsFromConfigs, ...savedBrands]));
            setSelectedBrands(allBrands);
          } else {
            setSelectedBrands(orgSettings.oe_brands || []);
            setConfigs([]);
          }
        } catch (orgError) {
          console.error('Failed to load organization settings:', orgError);
          // Fall back to user settings if organization settings fail
          setSettingsSource(backendSettings.settings_source || 'default');

          if (backendSettings.catalogs_config && backendSettings.catalogs_config.length > 0) {
            const loadedConfigs = backendSettings.catalogs_config.map((conf, index) => ({
              ...conf,
              _id: `conf-${Date.now()}-${index}`,
              catalog_type: 'parts_catalog'
            }));
            setConfigs(loadedConfigs);

            const brandsFromConfigs = loadedConfigs
              .map(c => c.catalog_name)
              .filter(name => OE_BRANDS.some(brand => brand.toUpperCase() === name.toUpperCase()));
            const savedBrands = backendSettings.oe_brands || [];
            const allBrands = Array.from(new Set([...brandsFromConfigs, ...savedBrands]));
            setSelectedBrands(allBrands);
          } else {
            setSelectedBrands(backendSettings.oe_brands || []);
            setConfigs([]);
          }
        }
      } else {
        // No organization - load user settings
        setSettingsSource(backendSettings.settings_source || 'default');

        if (backendSettings.catalogs_config && backendSettings.catalogs_config.length > 0) {
          const loadedConfigs = backendSettings.catalogs_config.map((conf, index) => ({
            ...conf,
            _id: `conf-${Date.now()}-${index}`,
            catalog_type: 'parts_catalog'
          }));
          setConfigs(loadedConfigs);

          const brandsFromConfigs = loadedConfigs
            .map(c => c.catalog_name)
            .filter(name => OE_BRANDS.some(brand => brand.toUpperCase() === name.toUpperCase()));
          const savedBrands = backendSettings.oe_brands || [];
          const allBrands = Array.from(new Set([...brandsFromConfigs, ...savedBrands]));
          setSelectedBrands(allBrands);
        } else {
          setSelectedBrands(backendSettings.oe_brands || []);
          setConfigs([]);
        }
      }
    } catch (error) {
      console.error('Failed to load settings:', error);
      showNotification('Error', t('settings.channels.errors.loadSettings', 'Failed to load settings'), 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const showNotification = (title: string, message: string, type: 'success' | 'error' | 'warning' | 'info') => {
    setNotification({ show: true, title, message, type });
    setTimeout(() => setNotification(prev => ({ ...prev, show: false })), 4000);
  };

  const addNewConfig = () => {
    const newConfig: UIConfig = {
      _id: `new-${Date.now()}`,
      catalog_name: '',
      catalog_user: '',
      catalog_password: '',
      catalog_type: 'parts_catalog',
      catalog_pricing_file: '',
      catalog_pricing_file_name: '',
      catalog_implementation: ''
    };
    setConfigs(prev => [...prev, newConfig]);
    setActiveConfigId(newConfig._id);
  };

  const removeConfig = (id: string) => {
    if (isUploading) return;

    // Check if we're removing a temp config
    if (tempConfig && tempConfig._id === id) {
      setTempConfig(null);
      setActiveConfigId(null);
      return;
    }

    const newConfigs = configs.filter(c => c._id !== id);
    setConfigs(newConfigs);

    if (activeConfigId === id) {
      setActiveConfigId(newConfigs.length > 0 ? newConfigs[0]._id : null);
    }

    // Update selectedBrands to reflect remaining configs
    const brandsFromConfigs = newConfigs
      .map(c => c.catalog_name)
      .filter(name => OE_BRANDS.some(brand => brand.toUpperCase() === name.toUpperCase()));
    setSelectedBrands(Array.from(new Set(brandsFromConfigs)));
  };

  const handleDeleteClick = (id: string) => {
    const configToDelete = tempConfig && tempConfig._id === id
      ? tempConfig
      : configs.find(c => c._id === id);

    if (!configToDelete) return;

    setDeleteConfirmation({
      show: true,
      configId: id,
      configName: configToDelete.catalog_name || t('settings.catalog.untitled', 'Untitled Catalog')
    });
  };

  const confirmDelete = async () => {
    const configId = deleteConfirmation.configId;
    if (!configId) return;

    // Close the confirmation dialog
    setDeleteConfirmation({ show: false, configId: null, configName: '' });

    // Check if we're removing a temp config (not saved yet)
    if (tempConfig && tempConfig._id === configId) {
      setTempConfig(null);
      setActiveConfigId(null);
      showNotification('Success', t('settings.catalog.deleteSuccess', 'Catalog configuration removed'), 'success');
      return;
    }

    // Remove from configs
    const newConfigs = configs.filter(c => c._id !== configId);

    // Update selectedBrands to reflect remaining configs
    const brandsFromConfigs = newConfigs
      .map(c => c.catalog_name)
      .filter(name => OE_BRANDS.some(brand => brand.toUpperCase() === name.toUpperCase()));

    // Save the updated settings
    setIsSaving(true);
    try {
      const configsToSave: BackendCatalogSettings[] = newConfigs
        .filter(c => c.source !== 'shared') // shared entries are managed by platform admins, never persisted here
        .filter(c => c.catalog_name && (c.catalog_user || c.catalog_password || c.catalog_implementation || c.has_credentials))
        .map(c => ({
          catalog_name: c.catalog_name,
          catalog_user: c._credentialsEdited ? c.catalog_user : '',
          catalog_password: c._credentialsEdited ? c.catalog_password : '',
          catalog_pricing_file: c.catalog_pricing_file || '',
          catalog_pricing_file_name: c.catalog_pricing_file_name || '',
          catalog_implementation: c.catalog_implementation || '',
          brands: c.brands || [],
          catalog_company: c.catalog_company || '',
          email_otp: c.email_otp || null
        }));

      // Save to organization or user settings based on current mode
      if (settingsSource === 'organization') {
        // Save to organization settings
        const orgSettings = {
          language: 'en',
          catalogs_config: configsToSave
        };
        await organizationService.updateOrganizationSettings(orgSettings);
      } else {
        // Save to personal settings
        const backendSettings: BackendUserSettingsUpdate = {
          language: 'en',
          catalogs_config: configsToSave,
          oe_brands: brandsFromConfigs,
        };
        await settingsService.updateUserSettings(backendSettings);
      }

      // Update local state
      setConfigs(newConfigs);
      setSelectedBrands(brandsFromConfigs);

      // Clear active config if it was the deleted one
      if (activeConfigId === configId) {
        setActiveConfigId(null);
      }

      showNotification(
        'Success',
        settingsSource === 'organization'
          ? t('settings.catalog.deleteSuccessOrg', 'Catalog configuration deleted and organization settings saved')
          : t('settings.catalog.deleteSuccess', 'Catalog configuration deleted and settings saved'),
        'success'
      );
    } catch (error) {
      console.error('Failed to save settings after delete:', error);
      showNotification('Error', t('settings.catalog.deleteError', 'Failed to delete catalog configuration'), 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const cancelDelete = () => {
    setDeleteConfirmation({ show: false, configId: null, configName: '' });
  };

  const updateActiveConfig = (field: keyof UIConfig, value: string | string[]) => {
    const isCredentialField = field === 'catalog_user' || field === 'catalog_password';

    // Shared entries are read-only — managed by platform admins
    const current = tempConfig && tempConfig._id === activeConfigId
      ? tempConfig
      : configs.find(c => c._id === activeConfigId);
    if (current?.source === 'shared') return;

    // Update temp config if active
    if (tempConfig && tempConfig._id === activeConfigId) {
      setTempConfig(prev => prev ? {
        ...prev,
        [field]: value,
        ...(isCredentialField ? { _credentialsEdited: true } : {})
      } : null);
      return;
    }

    // Update saved configs
    setConfigs(prev => prev.map(c => {
      if (c._id === activeConfigId) {
        return {
          ...c,
          [field]: value,
          ...(isCredentialField ? { _credentialsEdited: true } : {})
        };
      }
      return c;
    }));
  };

  const updateEmailOtp = (patch: Partial<EmailOtpConfig>) => {
    if (!activeConfigId) return;
    const update = (config: UIConfig): UIConfig => ({
      ...config,
      email_otp: {
        enabled: false,
        from: config.email_otp?.from || config.email_otp?.match?.sender?.value || '',
        subject: config.email_otp?.subject || config.email_otp?.match?.subject?.value || '',
        ...patch,
      },
    });
    if (tempConfig?._id === activeConfigId) setTempConfig(prev => prev ? update(prev) : null);
    else setConfigs(prev => prev.map(c => c._id === activeConfigId ? update(c) : c));
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!activeConfigId) return;
    const currentConfig = tempConfig && tempConfig._id === activeConfigId
      ? tempConfig
      : configs.find(c => c._id === activeConfigId);

    if (!currentConfig?.catalog_name) {
      showNotification('Error', t('settings.pricing.manufacturerRequired', 'Please enter a Manufacturer name before uploading.'), 'error');
      e.target.value = '';
      return;
    }

    const MAX_SIZE = 50 * 1024 * 1024;
    if (file.size > MAX_SIZE) {
      showNotification('Error', t('settings.pricing.fileTooLarge', 'File too large'), 'error');
      return;
    }

    setIsUploading(true);
    try {
      await settingsService.uploadPricingFile(currentConfig.catalog_name, file);

      // Update temp config if active
      if (tempConfig && tempConfig._id === activeConfigId) {
        setTempConfig(prev => prev ? {
          ...prev,
          catalog_pricing_file: 'uploaded',
          catalog_pricing_file_name: file.name
        } : null);
      } else {
        // Update saved config
        setConfigs(prev => prev.map(c => {
          if (c._id === activeConfigId) {
            return {
              ...c,
              catalog_pricing_file: 'uploaded',
              catalog_pricing_file_name: file.name
            };
          }
          return c;
        }));
      }

      showNotification('Success', t('settings.pricing.uploadSuccess', 'Pricing file uploaded successfully'), 'success');
    } catch (error: any) {
      console.error('Failed to upload pricing file:', error);
      const errorMessage = error?.response?.data?.detail || error;
      showNotification('Error', t('settings.pricing.uploadError', `Error: ${errorMessage}`), 'error');
    } finally {
      setIsUploading(false);
      e.target.value = '';
    }
  };

  const handleRemoveFile = () => {
    if (isUploading) return;

    // Update temp config if active
    if (tempConfig && tempConfig._id === activeConfigId) {
      setTempConfig(prev => prev ? {
        ...prev,
        catalog_pricing_file: '',
        catalog_pricing_file_name: ''
      } : null);
      return;
    }

    // Update saved config
    setConfigs(prev => prev.map(c => {
      if (c._id === activeConfigId) {
        return {
          ...c,
          catalog_pricing_file: '',
          catalog_pricing_file_name: ''
        };
      }
      return c;
    }));
  };

  const handleSave = async () => {
    // Combine saved configs with temp config (if it has credentials)
    let allConfigs = [...configs];

    if (tempConfig && (tempConfig.catalog_user || tempConfig.catalog_password || tempConfig.catalog_implementation)) {
      allConfigs.push(tempConfig);
    }

    const names = allConfigs.filter(c => c.source !== 'shared').map(c => c.catalog_name.trim().toUpperCase()).filter(Boolean);
    const dupName = names.find((n, i) => names.indexOf(n) !== i);
    if (dupName) {
      showNotification('Error', t('settings.catalog.duplicateName', 'Two catalogs are named {{name}} — give each a different name.', { name: dupName }), 'error');
      return;
    }

    const overlap = allConfigs.flatMap(c => brandConflicts(c, allConfigs).map(x => ({ ...x, mine: c.catalog_name })));
    if (overlap.length > 0) {
      const first = overlap[0];
      showNotification('Error', t('settings.catalog.brandOverlap', '{{brand}} is served by both {{a}} and {{b}}. A brand can belong to only one catalog — choose one.',
        { brand: first.brand, a: first.mine, b: first.other }), 'error');
      return;
    }

    setIsSaving(true);
    setSaveStatus('none');

    try {

      // Filter out configs that have no credentials (neither new nor existing)
      const validConfigs = allConfigs.filter(c =>
        c.catalog_name && (c.catalog_user || c.catalog_password || c.catalog_implementation || c.has_credentials)
      );

      // Shared entries are managed by platform admins and resolved at read
      // time — they must never be persisted into org/user settings.
      const configsToSave: BackendCatalogSettings[] = validConfigs.filter(c => c.source !== 'shared').map(c => ({
        catalog_name: c.catalog_name,
        // Only send credentials if the user actually edited them; otherwise send empty
        // strings so the backend knows to preserve the stored credentials.
        catalog_user: c._credentialsEdited ? c.catalog_user : '',
        catalog_password: c._credentialsEdited ? c.catalog_password : '',
        catalog_pricing_file: c.catalog_pricing_file || '',
        catalog_pricing_file_name: c.catalog_pricing_file_name || '',
        catalog_implementation: c.catalog_implementation || '',
        brands: c.brands || [],
        catalog_company: c.catalog_company || '',
        email_otp: c.email_otp || null
      }));

      // Update selectedBrands to include all brands from valid configs
      const brandsFromConfigs = validConfigs
        .map(c => c.catalog_name)
        .filter(name => OE_BRANDS.some(brand => brand.toUpperCase() === name.toUpperCase()));
      const updatedSelectedBrands = Array.from(new Set(brandsFromConfigs));

      // Save to organization or user settings based on current mode
      if (settingsSource === 'organization') {
        // Save to organization settings (backend only accepts language and catalogs_config)
        const orgSettings = {
          language: 'en', // Required field
          catalogs_config: configsToSave
        };
        await organizationService.updateOrganizationSettings(orgSettings);
      } else {
        // Save to personal settings
        const backendSettings: BackendUserSettingsUpdate = {
          language: 'en',
          catalogs_config: configsToSave,
          oe_brands: updatedSelectedBrands,
        };
        await settingsService.updateUserSettings(backendSettings);
      }

      // Update local state - replace all configs with saved ones and clear temp
      setConfigs(validConfigs.map((c, index) => ({
        ...c,
        _id: `conf-${Date.now()}-${index}` // Update IDs to match saved state
      })));

      // Clear temp config
      setTempConfig(null);

      // Update selected brands
      setSelectedBrands(updatedSelectedBrands);

      // If temp config was active, clear active config
      if (tempConfig && activeConfigId === tempConfig._id) {
        setActiveConfigId(null);
      } else if (activeConfigId && !validConfigs.find(c => c._id === activeConfigId)) {
        // Clear active config if it was removed
        setActiveConfigId(null);
      }

      setSaveStatus('success');
      setTimeout(() => setSaveStatus('none'), 3000);

      showNotification(
        t('settings.success', 'Success'),
        settingsSource === 'organization'
          ? t('settings.organization.orgSettingsSaved', 'Organization settings saved successfully')
          : t('settings.saveSuccess', 'Settings saved successfully'),
        'success'
      );
    } catch (error) {
      console.error('Failed to save settings:', error);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('none'), 5000);
      showNotification(
        'Error',
        t('settings.saveError', 'Failed to save settings'),
        'error'
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleTestConnection = async () => {
    if (!activeConfig) return;

    // Validate that required fields are filled
    if (!activeConfig.catalog_name) {
      showNotification('Error', t('settings.catalog.testError', 'Please enter a manufacturer name'), 'error');
      return;
    }

    if (!activeConfig.catalog_implementation) {
      showNotification('Error', t('settings.catalog.testError', 'Please select a catalog type'), 'error');
      return;
    }

    setIsTesting(true);
    setTestStatus('none');

    try {
      const testConfig = {
        catalog_name: activeConfig.catalog_name,
        catalog_user: activeConfig.catalog_user,
        catalog_password: activeConfig.catalog_password,
        catalog_implementation: activeConfig.catalog_implementation,
        brands: activeConfig.brands || [],
        catalog_company: activeConfig.catalog_company || '',
        email_otp: activeConfig.email_otp || null,
        catalog_pricing_file: activeConfig.catalog_pricing_file || '',
        catalog_pricing_file_name: activeConfig.catalog_pricing_file_name || ''
      };

      const response = await settingsService.testCatalogConfig(testConfig);

      if (response.data.status === 'success') {
        setTestStatus('success');
        showNotification('Success', t('settings.catalog.testSuccess', 'Connection successful!'), 'success');
        setTimeout(() => setTestStatus('none'), 3000);
      } else {
        setTestStatus('error');
        showNotification('Error', t('settings.catalog.testFailed', 'Connection failed'), 'error');
        setTimeout(() => setTestStatus('none'), 5000);
      }
    } catch (error: any) {
      console.error('Failed to test connection:', error);
      const errorMessage = error?.response?.data?.detail || error?.message || 'Connection failed';
      setTestStatus('error');
      showNotification('Error', errorMessage, 'error');
      setTimeout(() => setTestStatus('none'), 5000);
    } finally {
      setIsTesting(false);
    }
  };

  // Ask the vendor which catalogs the account is licensed for and let Gemini
  // map them onto the WMI brand list.
  const suggestBrands = async () => {
    if (!activeConfig || isSuggestingBrands) return;
    setIsSuggestingBrands(true);
    try {
      const res = await settingsService.suggestCatalogBrands({
        catalog_name: activeConfig.catalog_name,
        catalog_user: activeConfig.catalog_user,
        catalog_password: activeConfig.catalog_password,
        catalog_company: activeConfig.catalog_company || '',
        catalog_implementation: activeConfig.catalog_implementation,
        brands: activeConfig.brands || [],
        email_otp: activeConfig.email_otp || null,
        catalog_pricing_file: activeConfig.catalog_pricing_file || '',
        catalog_pricing_file_name: activeConfig.catalog_pricing_file_name || ''
      });
      // Brands another catalog already owns are left to it.
      const others = [...configs, ...(tempConfig ? [tempConfig] : [])].filter(c => c._id !== activeConfig._id);
      const owned = new Set(others.flatMap(catalogBrands).map(b => b.toUpperCase()));
      const all: string[] = res.data?.brands || [];
      const brands = all.filter(b => !owned.has(b.toUpperCase()));
      const skipped = all.filter(b => owned.has(b.toUpperCase()));
      updateActiveConfig('brands', brands);
      showNotification('Success', t('settings.catalog.brandsSuggested', '{{count}} brands selected from the catalog', { count: brands.length })
        + (skipped.length ? ' · ' + t('settings.catalog.brandsSkipped', 'already served elsewhere: {{brands}}', { brands: skipped.join(', ') }) : ''), 'success');
    } catch (error: any) {
      const errorMessage = error?.response?.data?.detail || error?.message || 'Could not read the catalog brands';
      showNotification('Error', errorMessage, 'error');
    } finally {
      setIsSuggestingBrands(false);
    }
  };

  // A second account of the same multi-brand vendor (another partslink24
  // login): a fresh entry under the next free "Multibrand N" name, renamable.
  const duplicateConfig = (source: UIConfig) => {
    const taken = new Set([...configs, ...(tempConfig ? [tempConfig] : [])].map(c => c.catalog_name.toUpperCase()));
    let n = 2;
    while (taken.has(`${MULTIBRAND} ${n}`.toUpperCase())) n++;
    const copy: UIConfig = {
      _id: `temp-${Date.now()}`,
      catalog_name: `${MULTIBRAND} ${n}`,
      catalog_user: '',
      catalog_password: '',
      catalog_type: 'parts_catalog',
      catalog_pricing_file: '',
      catalog_pricing_file_name: '',
      catalog_implementation: source.catalog_implementation,
      brands: [],
    };
    setTempConfig(copy);
    setActiveConfigId(copy._id);
  };

  const toggleBrand = (brand: string) => {
    // Check if a config already exists for this brand (case-insensitive)
    const existingConfig = configs.find(c => c.catalog_name.toUpperCase() === brand.toUpperCase());

    if (existingConfig) {
      // Load the existing config
      setActiveConfigId(existingConfig._id);
      setTempConfig(null); // Clear any temp config
    } else {
      // Create a temporary config (NOT added to configs - stays separate until save)
      const newTempConfig: UIConfig = {
        _id: `temp-${Date.now()}`,
        catalog_name: brand,
        catalog_user: '',
        catalog_password: '',
        catalog_type: 'parts_catalog',
        catalog_pricing_file: '',
        catalog_pricing_file_name: '',
        catalog_implementation: brand === MULTIBRAND ? MULTIBRAND_IMPLEMENTATIONS[0] : ''
      };
      setTempConfig(newTempConfig);
      setActiveConfigId(newTempConfig._id);
    }
  };

  const extraNames = configs.map(c => c.catalog_name)
    .filter(n => n && !OE_BRANDS.some(b => b.toUpperCase() === n.toUpperCase()));
  const isMultibrandName = (name: string) =>
    name === MULTIBRAND || needsBrands(configs.find(c => c.catalog_name.toUpperCase() === name.toUpperCase())?.catalog_implementation);
  const logoFor = (name: string) => getBrandLogo(isMultibrandName(name) ? MULTIBRAND : name);
  const filteredBrands = Array.from(new Set([...OE_BRANDS, ...extraNames])).filter(brand =>
    brand.toLowerCase().includes(brandSearchQuery.toLowerCase())
  ).sort((a, b) => {
    // Multibrand always first, then configured brands, then alphabetical
    if (a === MULTIBRAND) return -1;
    if (b === MULTIBRAND) return 1;
    if (isMultibrandName(a) !== isMultibrandName(b)) return isMultibrandName(a) ? -1 : 1;
    const aHasConfig = configs.some(c => c.catalog_name.toUpperCase() === a.toUpperCase());
    const bHasConfig = configs.some(c => c.catalog_name.toUpperCase() === b.toUpperCase());

    if (aHasConfig && !bHasConfig) return -1;
    if (!aHasConfig && bHasConfig) return 1;

    // If both configured or both not configured, sort alphabetically
    return a.localeCompare(b);
  });

  const isBrandConfig = (config: UIConfig) => {
    return OE_BRANDS.some(brand => brand.toUpperCase() === config.catalog_name.toUpperCase());
  };

  const activeConfig = tempConfig && tempConfig._id === activeConfigId
    ? tempConfig
    : configs.find(c => c._id === activeConfigId);

  // Shared entries come from platform-managed shared credentials — read-only here
  const isSharedConfig = activeConfig?.source === 'shared';
  const sharedConfigCount = sharedAssignments.length;

  // Load the WMI brand list the first time a multi-brand catalog is shown
  useEffect(() => {
    if (!activeConfig || !needsBrands(activeConfig.catalog_implementation) || wmiBrands.length > 0) return;
    settingsService.getWmiBrands()
      .then(res => setWmiBrands(res.data?.brands || []))
      .catch(err => console.error('Failed to load WMI brands:', err));
  }, [activeConfig?.catalog_implementation]);

  // Reset credential visibility when switching configs
  useEffect(() => {
    setCredentialsUnlocked(false);
    setShowPassword(false);
  }, [activeConfigId]);

  if (isLoading) {
    return (
      <AppLayout pageTitle={t('settings.catalog.title', 'OE Catalog')}>
        <div className="h-full flex flex-col bg-main-bg">
          <MobileHeader title={t('settings.catalog.title', 'OE Catalog Setup')} />

          <div className="flex items-center justify-center py-20">
            <div className="flex items-center gap-3">
              <Loader className="w-5 h-5 text-gray-400 animate-spin" />
              <span className="text-sm text-gray-500">{t('settings.loading', 'Loading settings...')}</span>
            </div>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout pageTitle={t('settings.catalog.title', 'OE Catalog')}>
      <div className="h-full flex flex-col bg-main-bg">
        <MobileHeader title={t('settings.catalog.title', 'OE Catalog Setup')} />

        {/* Sub-header with back button and search */}
        <div className="hidden sm:block bg-white border-b border-gray-100 px-4 sm:px-6 py-4">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between">
              <button
                onClick={onBack}
                className="text-sm text-gray-500 hover:text-gray-900 flex items-center gap-2 transition-colors group"
              >
                <ArrowLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
                {t('settings.backToSettings', 'Back to settings')}
              </button>
              {/* Search */}
              <div className="hidden md:block relative w-72">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={brandSearchQuery}
                  onChange={(e) => setBrandSearchQuery(e.target.value)}
                  placeholder={t('settings.catalog.searchBrands', 'Search brands...')}
                  className="w-full pl-10 pr-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900/10 focus:border-gray-300 transition-all bg-white"
                />
              </div>
            </div>
            {/* Mobile Search */}
            <div className="md:hidden mt-4 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={brandSearchQuery}
                onChange={(e) => setBrandSearchQuery(e.target.value)}
                placeholder={t('settings.catalog.searchBrands', 'Search brands...')}
                className="w-full pl-10 pr-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900/10 focus:border-gray-300 transition-all bg-white"
              />
            </div>

            {/* Organization Settings Indicator */}
            {organizationName && (
              <div className="mt-4">
                <div className="border rounded-lg p-3 bg-blue-50 border-blue-200">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-2 flex-1">
                      <Building2 className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-xs font-medium text-blue-900">
                          {t('settings.organization.editingOrgSettings', 'Editing organization settings for {{name}}', { name: organizationName })}
                        </p>
                        <p className="text-xs text-blue-700 mt-0.5">
                          {t('settings.organization.orgSettingsNote', 'Changes will affect all organization members')}
                        </p>
                      </div>
                    </div>
                    {sharedConfigCount > 0 && (
                      <span
                        className="inline-flex items-center gap-1.5 text-xs font-medium text-blue-700 bg-white border border-blue-200 px-3 py-1.5 rounded-lg whitespace-nowrap"
                        title={sharedAssignments.map(s => s.catalog_name).join(', ')}
                      >
                        <KeyRound className="w-3.5 h-3.5" />
                        {t('settings.organization.sharedAssigned', '{{count}} shared catalog(s) assigned by Catax', { count: sharedConfigCount })}
                      </span>
                    )}
                  </div>
                  {sharedConfigCount > 0 && (
                    <p className="text-xs text-blue-600 mt-2 flex items-center gap-1.5 flex-wrap">
                      <span className="font-medium">{t('settings.organization.sharedList', 'Shared:')}</span>
                      {sharedAssignments.map(s => (
                        <span key={s.slug || s.catalog_name} className="bg-white border border-blue-200 px-2 py-0.5 rounded-full">
                          {s.catalog_name}
                          {s.overridden && (
                            <span className="text-blue-400"> · {t('settings.organization.sharedOverridden', 'overridden by org credentials')}</span>
                          )}
                        </span>
                      ))}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Content */}
        {activeConfig ? (
          /* Configuration View */
          <div className="flex-1 overflow-y-auto">
            <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6">
              <button
                onClick={() => {
                  // Clear temp config when going back
                  setTempConfig(null);
                  setActiveConfigId(null);
                }}
                className="mb-6 text-sm text-gray-500 hover:text-gray-900 flex items-center gap-2 transition-colors group"
              >
                <ArrowLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
                {t('settings.catalog.backToBrands', 'Back to brands')}
              </button>

              {/* Notification */}
              {notification.show && (
                <div className="mb-6">
                  <div className={`p-4 rounded-xl border ${notification.type === 'success' ? 'bg-green-50 border-green-200 text-green-800' :
                    notification.type === 'error' ? 'bg-red-50 border-red-200 text-red-800' :
                      notification.type === 'warning' ? 'bg-yellow-50 border-yellow-200 text-yellow-800' :
                        'bg-blue-50 border-blue-200 text-blue-800'
                    }`}>
                    <div className="flex items-start gap-3">
                      <div className="flex-1">
                        <p className="font-medium text-sm">{notification.title}</p>
                        <p className="text-sm mt-0.5 opacity-90">{notification.message}</p>
                      </div>
                      <button onClick={() => setNotification(prev => ({ ...prev, show: false }))} className="text-current opacity-60 hover:opacity-100">
                        <X className="w-4 h-4" />
                      </button>
                    </div>

                  </div>
                </div>
              )}

              {/* Configuration Panel */}
              <div className="bg-white rounded-xl border border-gray-200/50 shadow-sm overflow-hidden">
                <div className="p-6">
                  {/* Header */}
                  <div className="flex items-center gap-4 mb-6 pb-6 border-b border-gray-100">
                    <div className="w-16 h-16 rounded-xl overflow-hidden bg-gray-50 border border-gray-200 flex items-center justify-center flex-shrink-0">
                      <img
                        src={needsBrands(activeConfig.catalog_implementation) ? getBrandLogo(MULTIBRAND) : getBrandLogo(activeConfig.catalog_name)}
                        alt={activeConfig.catalog_name}
                        className="w-14 h-14 object-contain"
                        onError={handleBrandLogoError(activeConfig.catalog_name)}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h2 className="text-xl font-semibold text-gray-900 truncate">{activeConfig.catalog_name || t('settings.catalog.untitled', 'Untitled Catalog')}</h2>
                      <p className="text-sm text-gray-500 mt-0.5">{t('settings.catalog.configuration', 'Configuration Details')}</p>
                    </div>
                    {isSharedConfig ? (
                      <span className="inline-flex items-center gap-1.5 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 px-3 py-1.5 rounded-lg">
                        <Building2 className="w-3.5 h-3.5" />
                        {t('settings.catalog.sharedBadge', 'Shared')}
                      </span>
                    ) : (
                      <>
                      {needsBrands(activeConfig.catalog_implementation) && (
                        <button
                          onClick={() => duplicateConfig(activeConfig)}
                          disabled={isUploading}
                          className="p-2 rounded-lg transition-all text-gray-400 hover:text-gray-900 hover:bg-gray-50"
                          title={t('settings.catalog.duplicate', 'Add another account of this catalog')}
                        >
                          <Copy className="w-5 h-5" />
                        </button>
                      )}
                      <button
                        onClick={() => handleDeleteClick(activeConfig._id)}
                        disabled={isUploading}
                        className={`p-2 rounded-lg transition-all ${isUploading
                          ? 'text-gray-300 cursor-not-allowed'
                          : 'text-gray-400 hover:text-red-600 hover:bg-red-50'
                          }`}
                        title={t('settings.catalog.remove', 'Remove catalog')}
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                      </>
                    )}
                  </div>

                  {/* Shared config notice */}
                  {isSharedConfig && (
                    <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg mb-6">
                      <Info className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                      <p className="text-xs text-blue-700">
                        {t('settings.catalog.sharedNote', 'This catalog uses shared credentials managed by Catax. Configuration is read-only.')}
                      </p>
                    </div>
                  )}

                  {/* Form */}
                  <div className="space-y-5">
                    {/* Manufacturer */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('settings.catalog.manufacturer', 'Manufacturer')}
                      </label>
                      <input
                        type="text"
                        value={activeConfig.catalog_name}
                        onChange={(e) => updateActiveConfig('catalog_name', e.target.value)}
                        className={`w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900/5 focus:border-gray-300 transition-all placeholder:text-gray-400 ${isBrandConfig(activeConfig)
                          ? 'bg-gray-50 cursor-not-allowed text-gray-600'
                          : 'bg-white'
                          }`}
                        placeholder={t('settings.catalog.manufacturerPlaceholder', 'e.g., KIA, BMW')}
                        disabled={isUploading || isBrandConfig(activeConfig) || isSharedConfig}
                        readOnly={isBrandConfig(activeConfig) || isSharedConfig}
                      />
                      {isBrandConfig(activeConfig) && (
                        <p className="text-xs text-gray-500 mt-1.5">
                          {t('settings.catalog.brandLocked', 'Selected from OE Brands list')}
                        </p>
                      )}
                      {!needsBrands(activeConfig.catalog_implementation) && (() => {
                        const conflicts = brandConflicts(activeConfig, [...configs, ...(tempConfig ? [tempConfig] : [])]);
                        return conflicts.length > 0 && (
                          <div className="flex items-start gap-2 p-2.5 mt-2 bg-red-50 border border-red-200 rounded-lg">
                            <AlertCircle className="w-3.5 h-3.5 text-red-600 flex-shrink-0 mt-0.5" />
                            <p className="text-xs text-red-700">
                              {t('settings.catalog.brandOverlapOther', '{{brand}} is already served by {{other}}. A brand can belong to only one catalog — choose one.',
                                { brand: conflicts[0].brand, other: conflicts[0].other })}
                            </p>
                          </div>
                        );
                      })()}
                    </div>

                    {/* Catalog Type */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('settings.catalog.label', 'Catalog')}
                      </label>
                      <select
                        value={activeConfig.catalog_implementation || ''}
                        onChange={(e) => updateActiveConfig('catalog_implementation', e.target.value)}
                        className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900/5 focus:border-gray-300 transition-all bg-white disabled:bg-gray-50 disabled:cursor-not-allowed disabled:text-gray-600"
                        disabled={isUploading || isSharedConfig}
                      >
                        <option value="">{t('settings.catalog.selectManufacturer', 'Select Catalog')}</option>
                        <option value="snaponepc">{t('settings.catalog.snaponepc', 'Snapon EPC')}</option>
                        <option value="saicmotor">{t('settings.catalog.saicMotor', 'Saic Motor')}</option>
                        <option value="excelerator">{t('settings.catalog.excelerator', 'Excelerator')}</option>
                        <option value="microcat">{t('settings.catalog.microcat', 'Microcat EPC')}</option>
                        <option value="xentry">{t('settings.catalog.xentry', 'XENTRY (Daimler Truck)')}</option>
                        <option value="partslink24">{t('settings.catalog.partslink24', 'partslink24')}</option>
                        <option value="asaweb">{t('settings.catalog.asaweb', 'Mitsubishi ASA (myASAweb)')}</option>
                        <option value="kgmobility">{t('settings.catalog.kgmobility', 'KG Mobility / SsangYong EPC')}</option>
                        <option value="geely">{t('settings.catalog.geely', 'Geely EPC (lepc.geely.com)')}</option>
                      </select>
                    </div>

                    {/* Bound brands — multi-brand catalogs only: multiselect over the WMI brand list */}
                    {needsBrands(activeConfig.catalog_implementation) && (() => {
                      const selected = activeConfig.brands || [];
                      const options = Array.from(new Set([...wmiBrands, ...selected]))
                        .filter(b => b.toLowerCase().includes(brandFilter.toLowerCase()))
                        .sort((a, b) => a.localeCompare(b));
                      const toggle = (brand: string) => updateActiveConfig('brands',
                        selected.includes(brand) ? selected.filter(b => b !== brand) : [...selected, brand]);
                      return (
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <label className="block text-sm font-medium text-gray-700">
                              {t('settings.catalog.brands', 'Brands served by this catalog')}
                              <span className="ml-2 text-xs font-normal text-gray-500">{selected.length} {t('settings.catalog.brandsSelected', 'selected')}</span>
                            </label>
                            <button
                              type="button"
                              onClick={suggestBrands}
                              disabled={isUploading || isSharedConfig || isSuggestingBrands}
                              className="text-xs font-medium px-3 py-1.5 rounded-lg border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                              title={t('settings.catalog.suggestBrandsHint', 'Reads the brands this account is licensed for and selects them')}
                            >
                              {isSuggestingBrands
                                ? t('settings.catalog.suggestingBrands', 'Detecting…')
                                : t('settings.catalog.suggestBrands', '✨ Auto-select from catalog')}
                            </button>
                          </div>
                          {selected.length > 0 && (
                            <div className="flex flex-wrap gap-1.5 mb-2">
                              {selected.map(b => (
                                <button key={b} type="button" onClick={() => toggle(b)} disabled={isUploading || isSharedConfig}
                                  className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-full bg-gray-900 text-white hover:bg-gray-700">
                                  {b}<span aria-hidden>×</span>
                                </button>
                              ))}
                            </div>
                          )}
                          <input
                            type="text"
                            value={brandFilter}
                            onChange={(e) => setBrandFilter(e.target.value)}
                            className="w-full px-4 py-2 text-sm border border-gray-200 rounded-t-lg focus:outline-none focus:ring-2 focus:ring-gray-900/5 focus:border-gray-300 bg-white placeholder:text-gray-400"
                            placeholder={t('settings.catalog.brandsFilter', 'Filter brands…')}
                            disabled={isUploading || isSharedConfig}
                          />
                          <div className="max-h-56 overflow-y-auto border border-t-0 border-gray-200 rounded-b-lg bg-white grid grid-cols-2 sm:grid-cols-3 gap-x-2">
                            {options.map(b => (
                              <label key={b} className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer">
                                <input type="checkbox" checked={selected.includes(b)} onChange={() => toggle(b)} disabled={isUploading || isSharedConfig} />
                                <span className="truncate">{b}</span>
                              </label>
                            ))}
                            {options.length === 0 && (
                              <p className="col-span-full px-3 py-2 text-xs text-gray-500">
                                {wmiBrands.length === 0 ? t('settings.catalog.brandsLoading', 'Loading brands…') : t('settings.catalog.brandsNoMatch', 'No brand matches')}
                              </p>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 mt-1.5">
                            {t('settings.catalog.brandsHint', 'A VIN decoding to any selected brand is searched in this catalog.')}
                          </p>
                          {(() => {
                            const conflicts = brandConflicts(activeConfig, [...configs, ...(tempConfig ? [tempConfig] : [])]);
                            return conflicts.length > 0 && (
                              <div className="flex items-start gap-2 p-2.5 mt-2 bg-red-50 border border-red-200 rounded-lg">
                                <AlertCircle className="w-3.5 h-3.5 text-red-600 flex-shrink-0 mt-0.5" />
                                <p className="text-xs text-red-700">
                                  {t('settings.catalog.brandOverlapInline', 'A brand can belong to only one catalog — choose one:')}{' '}
                                  {conflicts.map(c => `${c.brand} (${c.other})`).join(', ')}
                                </p>
                              </div>
                            );
                          })()}
                        </div>
                      );
                    })()}

                    {/* Credentials Section */}
                    <div className="rounded-xl border border-gray-200 overflow-hidden">
                      <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200">
                        <div className="flex items-center gap-2">
                          <Lock className="w-4 h-4 text-gray-500" />
                          <span className="text-sm font-medium text-gray-700">
                            {t('settings.credentials.title', 'Credentials')}
                          </span>
                        </div>
                        {isSharedConfig ? (
                          <span className="inline-flex items-center gap-1.5 text-xs font-medium text-blue-700">
                            <Building2 className="w-3.5 h-3.5" />
                            {t('settings.credentials.managedCentrally', 'Managed by Catax')}
                          </span>
                        ) : !credentialsUnlocked ? (
                          <button
                            type="button"
                            onClick={() => setCredentialsUnlocked(true)}
                            className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 px-3 py-1.5 rounded-lg hover:bg-amber-100 transition-colors"
                          >
                            <Unlock className="w-3.5 h-3.5" />
                            {t('settings.credentials.editCredentials', 'Edit Credentials')}
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => { setCredentialsUnlocked(false); setShowPassword(false); }}
                            className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-300 px-3 py-1.5 rounded-lg hover:bg-gray-50 transition-colors"
                          >
                            <Lock className="w-3.5 h-3.5" />
                            {t('settings.credentials.lockCredentials', 'Lock')}
                          </button>
                        )}
                      </div>

                      {!credentialsUnlocked ? (
                        /* Masked view */
                        <div className="px-4 py-4 space-y-3">
                          <div>
                            <p className="text-xs font-medium text-gray-500 mb-1.5">{t('settings.credentials.username', 'Username')}</p>
                            <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg border border-gray-200">
                              <User className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                              <span className="text-sm text-gray-500 font-mono tracking-widest">
                                {activeConfig.has_credentials || activeConfig.catalog_user
                                  ? '•'.repeat(12)
                                  : <span className="text-gray-400 font-sans tracking-normal text-xs italic">{t('settings.credentials.notSet', 'Not set')}</span>
                                }
                              </span>
                            </div>
                          </div>
                          <div>
                            <p className="text-xs font-medium text-gray-500 mb-1.5">{t('settings.credentials.password', 'Password')}</p>
                            <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg border border-gray-200">
                              <Lock className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                              <span className="text-sm text-gray-500 font-mono tracking-widest">
                                {activeConfig.has_credentials || activeConfig.catalog_password
                                  ? '••••••••••••'
                                  : <span className="text-gray-400 font-sans tracking-normal text-xs italic">{t('settings.credentials.notSet', 'Not set')}</span>
                                }
                              </span>
                            </div>
                          </div>
                          <p className="text-xs text-gray-400 flex items-center gap-1 mt-1">
                            <Info className="w-3 h-3" />
                            {isSharedConfig
                              ? t('settings.credentials.sharedHiddenNote', 'Shared credentials are managed by Catax and cannot be edited here')
                              : t('settings.credentials.hiddenNote', 'Click "Edit Credentials" to view or change credentials')}
                          </p>
                        </div>
                      ) : (
                        /* Editable view */
                        <div className="px-4 py-4 space-y-4">
                          <div className="flex items-start gap-2 p-2.5 bg-amber-50 border border-amber-200 rounded-lg mb-2">
                            <AlertCircle className="w-3.5 h-3.5 text-amber-600 flex-shrink-0 mt-0.5" />
                            <p className="text-xs text-amber-700">{t('settings.credentials.editingWarning', 'You are editing sensitive credentials. Lock them when done.')}</p>
                          </div>

                          {/* Company / client id — vendors whose login is (company, user, password) */}
                          {needsCompanyId(activeConfig.catalog_implementation) && (
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                {t('settings.credentials.company', 'Company ID')}
                              </label>
                              <input
                                type="text"
                                value={activeConfig.catalog_company || ''}
                                onChange={(e) => updateActiveConfig('catalog_company', e.target.value)}
                                className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900/5 focus:border-gray-300 transition-all bg-white placeholder:text-gray-400"
                                placeholder={t('settings.credentials.companyPlaceholder', 'Enter company / client id')}
                                disabled={isUploading}
                                autoComplete="off"
                              />
                            </div>
                          )}

                          {/* Username */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              {t('settings.credentials.username', 'Username')}
                            </label>
                            <input
                              type="text"
                              value={activeConfig.catalog_user}
                              onChange={(e) => updateActiveConfig('catalog_user', e.target.value)}
                              className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900/5 focus:border-gray-300 transition-all bg-white placeholder:text-gray-400"
                              placeholder={t('settings.credentials.usernamePlaceholder', 'Enter username')}
                              disabled={isUploading}
                              autoComplete="off"
                            />
                          </div>

                          {/* Password */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              {t('settings.credentials.password', 'Password')}
                            </label>
                            <div className="relative">
                              <input
                                type={showPassword ? 'text' : 'password'}
                                value={activeConfig.catalog_password}
                                onChange={(e) => updateActiveConfig('catalog_password', e.target.value)}
                                className="w-full px-4 py-2.5 pr-10 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900/5 focus:border-gray-300 transition-all bg-white placeholder:text-gray-400"
                                placeholder="••••••••"
                                disabled={isUploading}
                                autoComplete="new-password"
                              />
                              <button
                                type="button"
                                onClick={() => setShowPassword(p => !p)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700 transition-colors"
                                tabIndex={-1}
                              >
                                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="rounded-xl border border-gray-200 overflow-hidden">
                      <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200">
                        <div><span className="text-sm font-medium text-gray-700">Email verification</span><p className="text-xs text-gray-500">Accept codes only from expected messages.</p></div>
                        <input type="checkbox" checked={!!activeConfig.email_otp?.enabled} onChange={e => updateEmailOtp({ enabled: e.target.checked })} disabled={isSharedConfig} />
                      </div>
                      {activeConfig.email_otp?.enabled && <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                        <label className="text-xs text-gray-600">From<input type="email" value={activeConfig.email_otp.from || activeConfig.email_otp.match?.sender?.value || ''} onChange={e => updateEmailOtp({ from: e.target.value })} disabled={isSharedConfig} placeholder="forwarder@example.com" className="mt-1 w-full px-3 py-2 text-sm border rounded-lg" /></label>
                        <label className="text-xs text-gray-600">Subject contains<input value={activeConfig.email_otp.subject || activeConfig.email_otp.match?.subject?.value || ''} onChange={e => updateEmailOtp({ subject: e.target.value })} disabled={isSharedConfig} placeholder="MFA Email Verification" className="mt-1 w-full px-3 py-2 text-sm border rounded-lg" /></label>
                        <p className="md:col-span-2 text-xs text-gray-500">The verification code is extracted automatically with AI.</p>
                      </div>}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="mt-6 pt-6 border-t border-gray-100">
                    {/* Status Messages */}
                    <div className="mb-4 min-h-[28px]">
                      {saveStatus === 'success' && (
                        <div className="text-sm text-green-600 font-medium flex items-center gap-2">
                          <CheckCircle className="w-4 h-4" />
                          <span>{t('settings.saveSuccess', 'Settings saved successfully')}</span>
                        </div>
                      )}
                      {saveStatus === 'error' && (
                        <div className="text-sm text-red-600 font-medium flex items-center gap-2">
                          <AlertCircle className="w-4 h-4" />
                          <span>{t('settings.saveError', 'Failed to save settings')}</span>
                        </div>
                      )}
                      {testStatus === 'success' && (
                        <div className="text-sm text-green-600 font-medium flex items-center gap-2">
                          <CheckCircle className="w-4 h-4" />
                          <span>{t('settings.catalog.testSuccess', 'Connection successful!')}</span>
                        </div>
                      )}
                      {testStatus === 'error' && (
                        <div className="text-sm text-red-600 font-medium flex items-center gap-2">
                          <AlertCircle className="w-4 h-4" />
                          <span>{t('settings.catalog.testFailed', 'Connection failed')}</span>
                        </div>
                      )}
                    </div>

                    {/* Buttons */}
                    <div className="flex items-center gap-3">
                      <button
                        onClick={handleTestConnection}
                        disabled={isTesting || isSaving || isUploading}
                        className="inline-flex items-center justify-center gap-2 px-6 py-2.5 bg-white text-gray-700 text-sm font-medium rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                      >
                        {isTesting ? (
                          <>
                            <Loader className="w-4 h-4 animate-spin" />
                            {t('settings.catalog.testing', 'Testing...')}
                          </>
                        ) : (
                          <>
                            <Zap className="w-4 h-4" />
                            {t('settings.catalog.testConnection', 'Test Connection')}
                          </>
                        )}
                      </button>
                      <button
                        onClick={handleSave}
                        disabled={isSaving || isUploading || isTesting || isSharedConfig}
                        className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-2.5 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm"
                      >
                        {isSaving ? (
                          <>
                            <Loader className="w-4 h-4 animate-spin" />
                            {t('settings.saving', 'Saving...')}
                          </>
                        ) : (
                          <>
                            <Save className="w-4 h-4" />
                            {t('settings.saveSettings', 'Save Settings')}
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* Grid View - All Brands */
          <div className="flex-1 overflow-y-auto">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
              {/* Stats */}
              <div className="mb-6 flex items-center gap-2 text-sm text-gray-600">
                <span className="font-medium text-gray-900">{filteredBrands.length}</span>
                {t('settings.catalog.brandsAvailable', 'brands available')}
                {configs.length > 0 && (
                  <>
                    <span className="text-gray-300">•</span>
                    <span className="font-medium text-green-600">{configs.length}</span>
                    {t('settings.catalog.configured', 'configured')}
                  </>
                )}
              </div>

              {/* Brands Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
                {filteredBrands.map((brand) => {
                  const config = configs.find(c => c.catalog_name.toUpperCase() === brand.toUpperCase());
                  const hasConfig = !!config;

                  return (
                    <button
                      key={brand}
                      onClick={() => toggleBrand(brand)}
                      className={`relative group p-4 rounded-xl border transition-all ${hasConfig
                        ? 'bg-white border-gray-900/20 shadow-sm hover:shadow-md hover:border-gray-900/30'
                        : 'bg-white border-gray-200 hover:border-gray-300 hover:shadow-sm'
                        }`}
                    >
                      {/* Configured Badge */}
                      {hasConfig && (
                        <div className={`absolute top-2 right-2 w-2 h-2 rounded-full ${config?.source === 'shared' ? 'bg-blue-500' : 'bg-green-500'}`}></div>
                      )}

                      {/* Logo */}
                      <div className="aspect-square w-full mb-3 rounded-lg overflow-hidden bg-gray-50 flex items-center justify-center">
                        <img
                          src={logoFor(brand)}
                          alt={brand}
                          className="w-full h-full object-contain p-2"
                          onError={handleBrandLogoError(brand)}
                        />
                      </div>

                      {/* Brand Name */}
                      <div className="text-center">
                        <p className="text-sm font-medium text-gray-900 truncate">{brand}</p>
                        {hasConfig && (
                          config?.source === 'shared'
                            ? <p className="text-xs text-blue-600 mt-0.5">{t('settings.catalog.sharedBadge', 'Shared')}</p>
                            : <p className="text-xs text-green-600 mt-0.5">{t('settings.catalog.configured', 'Configured')}</p>
                        )}
                      </div>

                      {/* Hover Effect */}
                      <div className={`absolute inset-0 rounded-xl pointer-events-none transition-opacity ${hasConfig ? 'opacity-0' : 'opacity-0 group-hover:opacity-100'
                        }`}>
                        <div className="absolute inset-0 bg-gray-900/5 rounded-xl"></div>
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="bg-white rounded-full p-2 shadow-sm">
                            <Plus className="w-4 h-4 text-gray-700" />
                          </div>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>

              {filteredBrands.length === 0 && (
                <div className="text-center py-12">
                  <div className="inline-flex p-4 bg-gray-100 rounded-xl mb-4">
                    <Search className="w-8 h-8 text-gray-400" />
                  </div>
                  <p className="text-sm text-gray-500">{t('settings.catalog.noBrandsFound', 'No brands found')}</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirmation.show && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 animate-in fade-in zoom-in duration-200">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-red-100 rounded-xl flex-shrink-0">
                <AlertCircle className="w-6 h-6 text-red-600" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-semibold text-gray-900 mb-1">
                  {t('settings.catalog.deleteConfirmTitle', 'Delete Catalog Configuration?')}
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  {t('settings.catalog.deleteConfirmMessage', 'Are you sure you want to delete the configuration for')} <span className="font-medium text-gray-900">{deleteConfirmation.configName}</span>? {t('settings.catalog.deleteConfirmWarning', 'This action cannot be undone.')}
                </p>
                <div className="flex items-center gap-3">
                  <button
                    onClick={cancelDelete}
                    className="flex-1 px-4 py-2.5 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200 transition-all"
                  >
                    {t('settings.catalog.cancel', 'Cancel')}
                  </button>
                  <button
                    onClick={confirmDelete}
                    disabled={isSaving}
                    className="flex-1 px-4 py-2.5 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
                  >
                    {isSaving ? (
                      <>
                        <Loader className="w-4 h-4 animate-spin" />
                        {t('settings.catalog.deleting', 'Deleting...')}
                      </>
                    ) : (
                      <>
                        <Trash2 className="w-4 h-4" />
                        {t('settings.catalog.delete', 'Delete')}
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
};

export default CatalogSettingsPanel;
